/**
 * Global, breakpoint-aware inner-image slider controller.
 *
 * WHY THIS EXISTS
 * ----------------
 * collection-pagination.js boots the per-card inner sliders only on the
 * collection / search pages (cards inside #product-grid). Featured Collection,
 * Related Products and Recently Viewed live on other pages and use the CLASS
 * `.product-grid` (not the id `#product-grid`), so their inner sliders are
 * handled here instead. Cards inside #product-grid are skipped so this never
 * clashes with collection-pagination.js.
 *
 * PER-BREAKPOINT TOGGLES
 * ----------------------
 * card-product.liquid tags each card's .card__inner with
 * .inner-slider-desktop / .inner-slider-tablet / .inner-slider-mobile for every
 * breakpoint the merchant enabled. This script:
 *   - initialises the Swiper only when the CURRENT breakpoint is enabled,
 *   - destroys it when the current breakpoint is NOT enabled,
 *   - re-evaluates every card when the viewport crosses a breakpoint.
 * Combined with inner-slider-responsive.css (which hides the slider overlay at
 * disabled breakpoints), a disabled breakpoint shows a clean static image.
 *
 * Related Products (<product-recommendations> fetch) and Recently Viewed
 * (localStorage + fetch) inject their cards AFTER load, so we also watch the
 * DOM for newly inserted cards.
 *
 * Depends on window.initializeSwiper (custom.js) and window.Swiper
 * (swiper.min.js). No-ops safely if either is missing.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  // mobile: <=749 | tablet: 750-989 | desktop: >=990
  function currentBreakpoint() {
    if (window.matchMedia("(min-width: 990px)").matches) return "desktop";
    if (window.matchMedia("(min-width: 750px)").matches) return "tablet";
    return "mobile";
  }

  function isDesktopHover() {
    return window.matchMedia(
      "(min-width: 990px) and (hover: hover) and (pointer: fine)"
    ).matches;
  }

  function enabledForBreakpoint(card, bp) {
    var inner = card.querySelector(".card__inner");
    if (!inner || !inner.classList.contains("inner-slider-enabled")) return false;
    if (bp === "desktop") return inner.classList.contains("inner-slider-desktop");
    if (bp === "tablet") return inner.classList.contains("inner-slider-tablet");
    return inner.classList.contains("inner-slider-mobile");
  }

  function getContainer(card) {
    return (
      card.querySelector(".slide_imgs") ||
      card.querySelector(".slide_imgs-swiper")
    );
  }

  function getSwiper(card) {
    var el = card.querySelector(".swiper, .swiper-initialized");
    if (!el) return null;
    var sw = el.swiper || el._swiperInstance || null;
    return sw && !sw.destroyed ? sw : null;
  }

  function destroy(card) {
    var sw = getSwiper(card);
    if (sw) {
      try {
        sw.destroy(true, true);
      } catch (e) {}
    }
    var container = getContainer(card);
    if (container) container._swiperInstance = null;
    card.dataset.innerSliderInited = "0";
  }

  function bindHoverOnce(card) {
    if (card.dataset.innerHoverBound) return;
    card.dataset.innerHoverBound = "1";
    card.addEventListener("pointerenter", function () {
      var sw = getSwiper(card);
      if (sw && !sw.destroyed) sw.slideTo(1, 0);
    });
    card.addEventListener("pointerleave", function () {
      var sw = getSwiper(card);
      if (sw && !sw.destroyed) sw.slideTo(0, 0);
    });
  }

  // Decide init vs destroy for a single card at the current breakpoint.
  function evaluate(card) {
    if (!card || card.nodeType !== 1) return;
    if (card.closest && card.closest("#product-grid")) return; // collection page owns these
    var inner = card.querySelector(".card__inner");
    if (!inner || !inner.classList.contains("inner-slider-enabled")) return;

    var bp = currentBreakpoint();
    var wanted = enabledForBreakpoint(card, bp);

    if (!wanted) {
      if (getSwiper(card)) destroy(card);
      return;
    }

    // wanted === true
    if (getSwiper(card) && card.dataset.innerSliderInited === "1") return;

    var container = getContainer(card);
    if (!container) return;
    if (
      typeof window.initializeSwiper !== "function" ||
      typeof window.Swiper === "undefined"
    ) {
      return;
    }

    var swatch = card.querySelector(".color_product_main.active_color");
    try {
      window.initializeSwiper(container, swatch, card);
    } catch (e) {
      return;
    }
    card.dataset.innerSliderInited = "1";

    // Hover-to-second-image only makes sense on desktop pointer devices.
    if (bp === "desktop" && isDesktopHover()) bindHoverOnce(card);
  }

  function evaluateAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll(
        ".product-grid .card-wrapper, .recently-viewed-products .card-wrapper, .js-recentPdpBlock .card-wrapper"
      )
      .forEach(evaluate);
  }

  ready(function () {
    // 1) Server-rendered cards already present (e.g. Featured Collection).
    evaluateAll(document);

    // 2) Cards injected later (Related Products / Recently Viewed).
    //    Observe ONLY the specific containers that inject cards rather than the
    //    whole document, so this adds no overhead to unrelated DOM activity.
    var injectionTargets = document.querySelectorAll(
      ".recently-viewed-products, .js-recentPdpBlock, product-recommendations"
    );
    if (injectionTargets.length) {
      var mo = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!node || node.nodeType !== 1) continue;
            // Injected nodes are the <li>/wrapper elements that CONTAIN a card,
            // so search for .card-wrapper inside them directly. The observer is
            // already scoped to the injection containers, and evaluate() has its
            // own guards (#product-grid skip + inner-slider-enabled check), so
            // this only ever touches the right cards.
            if (node.matches && node.matches(".card-wrapper")) {
              evaluate(node);
            } else if (node.querySelectorAll) {
              node.querySelectorAll(".card-wrapper").forEach(evaluate);
            }
          }
        }
      });
      injectionTargets.forEach(function (target) {
        mo.observe(target, { childList: true, subtree: true });
      });
    }

    // 3) Re-evaluate every card when the viewport crosses a breakpoint.
    var lastBp = currentBreakpoint();
    var t;
    window.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var bp = currentBreakpoint();
        if (bp === lastBp) return; // only act on an actual breakpoint change
        lastBp = bp;
        evaluateAll(document);
      }, 200);
    });

    // 4) Timing-independent fallback. Recently Viewed and Related Products
    //    inject their cards via an async fetch that can land at unpredictable
    //    times (real network on the live site vs. cached/fast in the editor).
    //    Besides the observer, re-scan the whole document a few times after
    //    load so the cards are picked up whenever they arrive. evaluate() is
    //    idempotent (it skips cards already initialised), so this is cheap and
    //    safe, and it stops on its own.
    [400, 1000, 2000, 3500, 5000].forEach(function (delay) {
      setTimeout(function () {
        evaluateAll(document);
      }, delay);
    });

    // 5) Theme editor: re-scan whenever a section is re-rendered.
    document.addEventListener("shopify:section:load", function () {
      evaluateAll(document);
    });
  });
})();